<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="700"
    persistent
  >
    <div class="collection-excel-upload-modal" data-tour="bb-test-collection-excel-dialog">
      <div class="modal-header">
        <div class="header-left">
          <v-icon size="20">mdi-file-excel</v-icon>
          <span class="title">수납관리 엑셀 업로드</span>
        </div>
        <button class="btn-close" @click="closeDialog">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- 오해 방지 안내 -->
        <div class="upload-notice">
          <v-icon size="16">mdi-information-outline</v-icon>
          <p>이 업로드는 수급자별 <strong>청구</strong>를 등록해 <strong>미수금</strong>을 관리하는 용도입니다. 통장에 입금된 돈은 통장내역에서 관리하고, 여기서는 청구와 입금을 맞춰 미수만 확인합니다.</p>
        </div>

        <!-- 연월 선택 -->
        <div class="section">
          <div class="section-title">업로드 대상 연월</div>
          <div class="month-selector">
            <input 
              type="month" 
              v-model="selectedMonth"
              class="month-input"
            />
          </div>
        </div>
        
        <!-- 양식 다운로드 -->
        <div class="section">
          <div class="section-title">1. 엑셀 양식 다운로드</div>
          <div class="section-content">
            <p class="description">아래 양식을 다운로드하여 수납관리 데이터를 작성하세요.</p>
            <button class="btn-download" @click="downloadTemplate">
              <v-icon size="16">mdi-download</v-icon>
              <span>양식 다운로드</span>
            </button>
          </div>
        </div>
        
        <!-- 양식 설명 -->
        <div class="section">
          <div class="section-title">양식 설명</div>
          <div class="template-info">
            <table class="info-table">
              <thead>
                <tr>
                  <th>컬럼</th>
                  <th>설명</th>
                  <th>필수</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>수급자명</td>
                  <td>수급자 이름 (미등록시 자동 등록)</td>
                  <td class="required">필수</td>
                </tr>
                <tr>
                  <td>인정관리번호</td>
                  <td>장기요양인정번호 (예: L2312017237)</td>
                  <td>선택</td>
                </tr>
                <tr>
                  <td>항목</td>
                  <td>본인부담금수입, 식재료비수입, 상급침실이용료, 이미용비, 기타비급여수입</td>
                  <td class="required">필수</td>
                </tr>
                <tr>
                  <td>청구금액</td>
                  <td>청구할 금액 (숫자만)</td>
                  <td class="required">필수</td>
                </tr>
                <tr>
                  <td>청구일</td>
                  <td>청구 날짜 (YYYY-MM-DD 형식)</td>
                  <td>선택</td>
                </tr>
                <tr>
                  <td>비고</td>
                  <td>메모</td>
                  <td>선택</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <!-- 파일 업로드 -->
        <div class="section">
          <div class="section-title">2. 엑셀 파일 업로드</div>
          <div class="section-content">
            <div 
              class="upload-area"
              :class="{ 'dragging': isDragging, 'has-file': uploadedFile }"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleDrop"
              @click="triggerFileInput"
            >
              <input 
                type="file" 
                ref="fileInputRef"
                accept=".xlsx,.xls"
                @change="handleFileSelect"
                style="display: none"
              />
              <template v-if="!uploadedFile">
                <v-icon size="40" color="#94a3b8">mdi-file-upload-outline</v-icon>
                <p class="upload-text">클릭하거나 파일을 여기에 드롭하세요</p>
                <p class="upload-hint">.xlsx, .xls 파일만 가능</p>
              </template>
              <template v-else>
                <v-icon size="32" color="#059669">mdi-file-excel</v-icon>
                <p class="file-name">{{ uploadedFile.name }}</p>
                <p class="file-size">{{ formatFileSize(uploadedFile.size) }}</p>
                <button class="btn-remove-file" @click.stop="removeFile">
                  <v-icon size="14">mdi-close</v-icon>
                  파일 제거
                </button>
              </template>
            </div>
          </div>
        </div>
        
        <!-- 미리보기 -->
        <div class="section" v-if="previewData.length > 0">
          <div class="section-title">
            미리보기 
            <span class="preview-count">({{ previewData.length }}건)</span>
          </div>
          <div class="preview-table-wrap">
            <table class="preview-table">
              <thead>
                <tr>
                  <th>수급자명</th>
                  <th>인정관리번호</th>
                  <th>항목</th>
                  <th>청구금액</th>
                  <th>청구일</th>
                  <th>비고</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in previewData.slice(0, 10)" :key="idx">
                  <td>{{ row.name }}</td>
                  <td>{{ row.ltcNo || '-' }}</td>
                  <td>{{ row.itemName }}</td>
                  <td class="text-right">{{ formatNumber(row.charge) }}</td>
                  <td>{{ row.chargeDate || '-' }}</td>
                  <td>{{ row.memo || '-' }}</td>
                </tr>
                <tr v-if="previewData.length > 10" class="more-row">
                  <td colspan="6">... 외 {{ previewData.length - 10 }}건 더 있음</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <!-- 업로드 에러 표시 (성공 시에는 alert 후 닫힘) -->
        <div class="section" v-if="uploadResult && !uploadResult.success">
          <div class="section-title">업로드 오류</div>
          <div class="result-box error">
            <v-icon size="20">mdi-alert-circle</v-icon>
            <div class="result-content">
              <p class="result-message">{{ uploadResult.message }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-cancel" @click="closeDialog">닫기</button>
        <button 
          class="btn-upload" 
          @click="uploadData"
          :disabled="!canUpload || isUploading"
        >
          <v-progress-circular v-if="isUploading" indeterminate size="16" width="2" />
          <v-icon v-else size="16">mdi-upload</v-icon>
          <span>{{ isUploading ? '업로드 중...' : '업로드' }}</span>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import * as XLSX from 'xlsx';
import { collectionApi } from '@/services/api';

interface Props {
  modelValue: boolean;
  memberCode: string | number;
  year?: number | string;
  month?: number | string;
  bizGubun?: string;
}

const props = withDefaults(defineProps<Props>(), {
  year: () => new Date().getFullYear(),
  month: () => new Date().getMonth() + 1
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'uploaded'): void;
}>();

// 다이얼로그 상태
const dialogOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

// 연월 선택
const selectedMonth = ref('');

// 파일 업로드 관련
const fileInputRef = ref<HTMLInputElement | null>(null);
const uploadedFile = ref<File | null>(null);
const isDragging = ref(false);
const isUploading = ref(false);

// 미리보기 데이터
interface PreviewItem {
  name: string;
  ltcNo?: string;
  itemName: string;
  charge: number;
  chargeDate?: string;
  memo?: string;
}
const previewData = ref<PreviewItem[]>([]);

// 업로드 결과 (에러 표시용)
interface UploadResult {
  success: boolean;
  message: string;
}
const uploadResult = ref<UploadResult | null>(null);

// 업로드 가능 여부
const canUpload = computed(() => {
  return uploadedFile.value && previewData.value.length > 0 && selectedMonth.value;
});

// 초기화
watch(() => props.modelValue, (val) => {
  if (val) {
    // 모달 열릴 때 초기화
    const y = typeof props.year === 'string' ? parseInt(props.year) : (props.year || new Date().getFullYear());
    const m = typeof props.month === 'string' ? parseInt(props.month) : (props.month || new Date().getMonth() + 1);
    selectedMonth.value = `${y}-${String(m).padStart(2, '0')}`;
    uploadedFile.value = null;
    previewData.value = [];
    uploadResult.value = null;
  }
});

// 다이얼로그 닫기
const closeDialog = () => {
  dialogOpen.value = false;
};

// 파일 선택 트리거
const triggerFileInput = () => {
  fileInputRef.value?.click();
};

// 파일 선택 핸들러
const handleFileSelect = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    processFile(input.files[0]);
  }
};

// 드롭 핸들러
const handleDrop = (event: DragEvent) => {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files[0]) {
    processFile(files[0]);
  }
};

// 파일 처리
const processFile = async (file: File) => {
  // 확장자 체크
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!['xlsx', 'xls'].includes(ext || '')) {
    alert('xlsx 또는 xls 파일만 업로드 가능합니다.');
    return;
  }
  
  uploadedFile.value = file;
  uploadResult.value = null;
  
  // 엑셀 파싱
  try {
    const data = await readExcelFile(file);
    previewData.value = data;
  } catch (error) {
    console.error('엑셀 파싱 오류:', error);
    alert('엑셀 파일을 읽는 중 오류가 발생했습니다.');
    uploadedFile.value = null;
    previewData.value = [];
  }
};

// 엑셀 파일 읽기
const readExcelFile = (file: File): Promise<PreviewItem[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
        
        // 첫 번째 행은 헤더, 두 번째 행부터 데이터
        const items: PreviewItem[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || !row[0]) continue; // 빈 행 스킵
          
          const name = String(row[0] || '').trim();
          const ltcNo = String(row[1] || '').trim();
          const itemName = String(row[2] || '').trim();
          const charge = parseNumber(row[3]);
          const chargeDate = formatExcelDate(row[4]);
          const memo = String(row[5] || '').trim();
          
          if (name && itemName) {
            items.push({ name, ltcNo, itemName, charge, chargeDate, memo });
          }
        }
        
        resolve(items);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// 엑셀 날짜 변환
const formatExcelDate = (value: any): string => {
  if (!value) return '';
  
  // 이미 문자열인 경우
  if (typeof value === 'string') {
    // YYYY-MM-DD 형식 체크
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    // YYYY/MM/DD 형식
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(value)) return value.replace(/\//g, '-');
    return value;
  }
  
  // 엑셀 날짜 숫자인 경우
  if (typeof value === 'number') {
    const date = XLSX.SSF.parse_date_code(value);
    if (date) {
      return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
    }
  }
  
  return '';
};

// 파일 제거
const removeFile = () => {
  uploadedFile.value = null;
  previewData.value = [];
  uploadResult.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

// 양식 다운로드
const downloadTemplate = () => {
  // 양식 데이터 생성
  const templateData = [
    ['수급자명', '인정관리번호', '항목', '청구금액', '청구일', '비고'],
    ['홍길동', 'L2312017237', '본인부담금수입', 150000, '2025-01-15', '1월분'],
    ['홍길동', 'L2312017237', '식재료비수입', 50000, '2025-01-15', ''],
    ['김철수', 'L2401005123', '본인부담금수입', 120000, '2025-01-15', '1월분'],
  ];
  
  const ws = XLSX.utils.aoa_to_sheet(templateData);
  
  // 컬럼 너비 설정
  ws['!cols'] = [
    { wch: 12 },  // 수급자명
    { wch: 15 },  // 인정관리번호
    { wch: 18 },  // 항목
    { wch: 12 },  // 청구금액
    { wch: 12 },  // 청구일
    { wch: 20 },  // 비고
  ];
  
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '수납관리');
  
  XLSX.writeFile(wb, '수납관리_양식.xlsx');
};

// 데이터 업로드
const uploadData = async () => {
  if (!canUpload.value) return;
  
  const [year, month] = selectedMonth.value.split('-').map(Number);
  
  isUploading.value = true;
  uploadResult.value = null;
  
  try {
    const response = await collectionApi.uploadExcel(props.memberCode, {
      items: previewData.value,
      year,
      month,
      bizGubun: props.bizGubun
    });
    
    if (response.data?.success) {
      const data = response.data.data;
      
      // 결과 메시지 생성
      const resultParts = [];
      if (data?.newBeneficiaries > 0) {
        resultParts.push(`신규 수급자 ${data.newBeneficiaries}명 등록`);
      }
      if (data?.insertedCollections > 0) {
        resultParts.push(`수납관리 ${data.insertedCollections}건 등록`);
      }
      
      const resultMessage = resultParts.length > 0 
        ? `업로드 완료!\n${resultParts.join('\n')}`
        : '업로드가 완료되었습니다.';
      
      alert(resultMessage);
      
      // 성공 시 부모 컴포넌트에 알림 후 모달 닫기
      emit('uploaded');
      closeDialog();
    } else {
      uploadResult.value = {
        success: false,
        message: response.data?.message || '업로드 중 오류가 발생했습니다.'
      };
    }
  } catch (error: any) {
    uploadResult.value = {
      success: false,
      message: error.response?.data?.message || error.message || '업로드 중 오류가 발생했습니다.'
    };
  } finally {
    isUploading.value = false;
  }
};

// 숫자 파싱
const parseNumber = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const num = parseInt(String(value).replace(/[^0-9-]/g, ''));
  return isNaN(num) ? 0 : num;
};

// 숫자 포맷
const formatNumber = (value: number): string => {
  return value.toLocaleString('ko-KR');
};

// 파일 크기 포맷
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};
</script>
